import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Loader2, ExternalLink, Bookmark, Trash2, Radar } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const s = Math.max(0, (Date.now() - then) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return iso; }
}

export default function Browsed() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/opportunities/browsed/list");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Failed to load browsed opportunities");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const promote = async (nid) => {
    setBusy(nid + ":promote");
    try {
      await api.post(`/opportunities/browsed/${nid}/promote`);
      toast.success("Saved to pipeline");
      setRows((r) => r.filter((x) => x.notice_id !== nid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const dismiss = async (nid) => {
    // Optimistically remove and offer a 5s undo before actually deleting.
    const target = rows.find((x) => x.notice_id === nid);
    if (!target) return;
    setRows((r) => r.filter((x) => x.notice_id !== nid));
    let undone = false;
    const t = setTimeout(async () => {
      if (undone) return;
      try { await api.delete(`/opportunities/browsed/${nid}`); } catch { toast.error("Failed to remove"); }
    }, 5000);
    toast("Removed from Recently Viewed", {
      duration: 5000,
      action: {
        label: "UNDO",
        onClick: () => {
          undone = true;
          clearTimeout(t);
          setRows((r) => {
            if (r.some((x) => x.notice_id === nid)) return r;
            return [target, ...r];
          });
          toast.success("Restored");
        },
      },
    });
  };

  return (
    <div className="min-h-screen p-6 sm:p-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <div className="bf-eyebrow text-sky-600">// RECENTLY VIEWED</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl">Browsed Opportunities</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              SAM.gov opportunities you've opened but never explicitly saved to your pipeline. Save the ones worth pursuing, dismiss the rest.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="browsed-loading"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="browsed-empty">
            <Radar className="w-8 h-8 mx-auto text-slate-400 mb-3" />
            <div className="font-display text-lg mb-1">Nothing browsed yet</div>
            <div className="text-sm text-slate-500 mb-4">Open an opportunity from Search or Forecaster and it will land here.</div>
            <Link to="/opportunities?tab=search" className="bf-pill inline-flex" data-testid="browsed-cta-search">Search SAM.gov</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="browsed-list">
            {rows.map((o) => (
              <div key={o.notice_id} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-4" data-testid={`browsed-row-${o.notice_id}`}>
                <Link to={`/opportunities/${o.notice_id}`} className="min-w-0 flex-1 block">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-3 flex-wrap">
                    <span>{o.agency || "—"}</span>
                    {o.state && <span className="text-sky-600">{o.state}</span>}
                    {o.response_deadline && <span>DUE {new Date(o.response_deadline).toLocaleDateString()}</span>}
                    <span className="text-slate-400" data-testid={`browsed-viewed-${o.notice_id}`}>Viewed {timeAgo(o.last_viewed_at || o.created_at)}</span>
                  </div>
                  <div className="font-display font-semibold text-lg mb-1">{o.title}</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {o.naics && <span className="text-[11px] font-mono uppercase tracking-widest text-indigo-700 border border-indigo-200 bg-indigo-50 rounded px-2 py-0.5">NAICS {o.naics}</span>}
                    {o.set_aside && <span className="text-[11px] font-mono uppercase tracking-widest text-amber-700 border border-amber-200 bg-amber-50 rounded px-2 py-0.5 truncate max-w-[280px]">{o.set_aside}</span>}
                  </div>
                </Link>
                <div className="text-right shrink-0 flex flex-col items-end gap-2">
                  <div>
                    <div className={`font-display font-black text-3xl ${o.match_score >= 85 ? "text-emerald-600" : o.match_score >= 70 ? "text-amber-600" : "text-slate-400"}`}>{o.match_score}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-right">MATCH</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => promote(o.notice_id)}
                      disabled={busy === o.notice_id + ":promote"}
                      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      data-testid={`browsed-save-${o.notice_id}`}
                    >
                      {busy === o.notice_id + ":promote" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}Save to Pipeline
                    </button>
                    <button
                      onClick={() => dismiss(o.notice_id)}
                      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                      data-testid={`browsed-dismiss-${o.notice_id}`}
                    >
                      <Trash2 className="w-3 h-3" />Dismiss
                    </button>
                    {o.url && <a href={o.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded text-slate-500 hover:text-sky-600"><ExternalLink className="w-3 h-3" />SAM</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
